import { OrbindApiError } from '@orbind/api-client'
import { Button, FieldBlock, Input, Modal, useToast } from '@orbind/ui'
import { useState } from 'react'
import { api } from '../../lib/api.ts'

const SENHA_MINIMA = 8

/** Troca de senha do usuário logado — exige a senha atual, como qualquer sistema sério. */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const [atual, setAtual] = useState('')
  const [nova, setNova] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erroAtual, setErroAtual] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const limpar = () => {
    setAtual('')
    setNova('')
    setConfirmacao('')
    setErroAtual(null)
  }

  const naoConfere = confirmacao !== '' && nova !== confirmacao
  const curtaDemais = nova !== '' && nova.length < SENHA_MINIMA
  const podeSalvar = atual !== '' && nova.length >= SENHA_MINIMA && nova === confirmacao && !salvando

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    setErroAtual(null)
    try {
      await api.auth['change-password'].$post({ json: { currentPassword: atual, newPassword: nova } })
      toast.push({ tone: 'success', message: 'Senha alterada.' })
      limpar()
      onClose()
    } catch (erro) {
      if (erro instanceof OrbindApiError && erro.status === 422) {
        setErroAtual('Senha atual incorreta.')
      } else {
        toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível trocar a senha.' })
      }
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        limpar()
        onClose()
      }}
      title="Alterar senha"
      width={420}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!podeSalvar} onClick={() => void salvar()}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="ob-stack">
        <FieldBlock label="Senha atual" required error={erroAtual}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="password"
              autoFocus
              autoComplete="current-password"
              invalid={invalid}
              value={atual}
              onChange={(e) => {
                setAtual(e.target.value)
                setErroAtual(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && podeSalvar) void salvar()
              }}
            />
          )}
        </FieldBlock>

        <FieldBlock label="Nova senha" required hint={`Ao menos ${SENHA_MINIMA} caracteres.`}>
          {({ id }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              invalid={curtaDemais}
              value={nova}
              onChange={(e) => setNova(e.target.value)}
            />
          )}
        </FieldBlock>

        <FieldBlock label="Confirmar nova senha" required error={naoConfere ? 'As senhas não conferem.' : null}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              invalid={invalid}
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && podeSalvar) void salvar()
              }}
            />
          )}
        </FieldBlock>
      </div>
    </Modal>
  )
}
